import { formatDate } from '../../../services/primitives/date-utils';
import { ProviderPatientClaimStatement } from '../../../api/server/models/ProviderPatientClaimStatement';
import { formatMoney, formatCurrency, parseMoney } from '../utils/statement-format.utils';
import { extractDiagnosisCodes, isInRange } from '../utils/statement-filter.utils';
import {
    isStatementClaimPendingAdjudication,
    resolveStatementClaimFinancials,
} from '../utils/statement-claim-financials.utils';
import {
    BuildStatementClaimCardsArgs,
    StatementClaimCard,
    StatementClaimCardLine,
    StatementClaimFlowRow,
} from './statement-claim-cards.types';
import type { ProviderClaim } from '../../../api/server/models/ProviderClaim';

const formatDosLabel = (fromDos?: string, throughDos?: string): string | undefined => {
    if (!fromDos) return undefined;
    const fromLabel = formatDate(fromDos, 'MMM dd, yyyy');
    if (!throughDos || throughDos === fromDos) return fromLabel;
    return `${fromLabel} – ${formatDate(throughDos, 'MMM dd, yyyy')}`;
};

const formatProviders = (claim: ProviderPatientClaimStatement): string | undefined => {
    const names = (claim.renderingProviders || [])
        .map(provider => [provider.firstName, provider.lastName].filter(Boolean).join(' '))
        .filter(Boolean);
    return names.length > 0 ? names.join(', ') : undefined;
};

const getPayerName = (
    claim: ProviderPatientClaimStatement,
    providerClaimsById: BuildStatementClaimCardsArgs['providerClaimsById'],
): string | undefined => {
    const fromPayments = (claim.payerPayments || []).map(p => p.insuranceName).find(Boolean);
    if (fromPayments) return fromPayments;
    if (claim.id) {
        return providerClaimsById.get(claim.id)?.primaryPayerName;
    }
    return undefined;
};

/** Same status string used by Statements tab claim cards (and print claim headers). */
export const getStatementClaimStatusLabel = (
    claim: Pick<ProviderPatientClaimStatement, 'id' | 'pendingAdjudication' | 'payerPayments'>,
    providerClaimsById: BuildStatementClaimCardsArgs['providerClaimsById'],
): string | undefined => {
    const providerClaim = claim.id ? providerClaimsById.get(claim.id) : undefined;
    if (isStatementClaimPendingAdjudication(claim, providerClaim)) return 'Pending';
    if (!claim.id) return undefined;
    const responsibility = providerClaim?.claimResponsibilityType;
    if (!responsibility) return undefined;
    return String(responsibility).replace(/_/g, ' ');
};

export const getStatementStatusTone = (statusLabel?: string): 'warning' | 'default' =>
    statusLabel?.toLowerCase().includes('pending') ? 'warning' : 'default';

const buildServiceLines = (
    claim: ProviderPatientClaimStatement,
    professionals: BuildStatementClaimCardsArgs['professionals'],
): StatementClaimCardLine[] => {
    const professional = professionals.find(item => item.claimId === claim.id);

    return (claim.serviceLines || []).map((line, index) => {
        let diagnosisCodes: string[] = [];
        if (professional?.professionalTemplate) {
            const serviceLineDetails = professional.professionalTemplate.f24ServiceLine?.find(
                item => item.id === line.id,
            );
            if (serviceLineDetails) {
                const diagnosisCodeMap = extractDiagnosisCodes(professional.professionalTemplate);
                diagnosisCodes = (serviceLineDetails.f24eDiagnosisPointers || [])
                    .map((letter: string) => diagnosisCodeMap[letter.toLowerCase()])
                    .filter(Boolean);
            }
        }

        return {
            id: line.id || `${claim.id || claim.externalId || 'claim'}-line-${index}`,
            code: line.procedure,
            description: line.description,
            charge: line.chargeAmount?.amount ? formatCurrency(line.chargeAmount.amount) : undefined,
            diagnosisCodes,
        };
    });
};

const buildFlowRows = (
    claim: ProviderPatientClaimStatement,
    providerClaim?: ProviderClaim,
): StatementClaimFlowRow[] => {
    const { charge, insurance, adjustment } = resolveStatementClaimFinancials(claim, providerClaim);
    const patientPaid = (claim.patientPayments || []).reduce(
        (sum, payment) => sum + parseMoney(payment.amount?.amount),
        0,
    );
    const discount = (claim.discountLines || []).reduce(
        (sum, line) => sum + parseMoney(line.amount?.amount),
        0,
    );
    const balance = claim.balanceDue?.amount
        ? parseMoney(claim.balanceDue.amount)
        : Math.max(0, charge - insurance - adjustment - patientPaid - discount);

    const rows: StatementClaimFlowRow[] = [
        {
            id: 'charge',
            label: 'Billed charges',
            amount: formatMoney(charge),
        },
    ];

    if (isStatementClaimPendingAdjudication(claim, providerClaim)) {
        rows.push({
            id: 'pending',
            label: 'Awaiting payer adjudication',
            amount: '—',
            tone: 'muted',
        });
        return rows;
    }

    if (adjustment > 0) {
        rows.push({
            id: 'adjustment',
            label: 'Plan adjustment',
            amount: formatMoney(adjustment, { negative: true }),
            tone: 'muted',
        });
    }

    if (insurance > 0) {
        rows.push({
            id: 'insurance',
            label: 'Insurance paid',
            amount: formatMoney(insurance, { negative: true }),
            tone: 'credit',
        });
    }

    if (discount > 0) {
        rows.push({
            id: 'discount',
            label: 'Discount',
            amount: formatMoney(discount, { negative: true }),
            tone: 'credit',
        });
    }

    if (patientPaid > 0) {
        rows.push({
            id: 'patient-paid',
            label: 'Patient paid',
            amount: formatMoney(patientPaid, { negative: true }),
            tone: 'credit',
        });
    }

    rows.push({
        id: 'balance',
        label: 'Balance due on claim',
        amount: formatMoney(balance),
        tone: balance > 0 ? 'emphasis' : 'default',
        emphasized: true,
        dividerTop: true,
    });

    return rows;
};

const matchesSearch = (
    claim: ProviderPatientClaimStatement,
    lines: StatementClaimCardLine[],
    searchQuery: string,
): boolean => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const haystack = [
        claim.externalId,
        claim.id,
        ...(lines.flatMap(line => [line.code, line.description])),
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

    return haystack.includes(query);
};

export const buildStatementClaimCards = (args: BuildStatementClaimCardsArgs): StatementClaimCard[] => {
    const {
        claims,
        professionals,
        providerClaimsById,
        dosFilterFrom,
        dosFilterTo,
        searchQuery,
        getClaimRowHref,
    } = args;

    return claims
        .filter(claim => isInRange(claim.fromDos, dosFilterFrom, dosFilterTo))
        .map(claim => {
            const lines = buildServiceLines(claim, professionals);
            const balanceValue = claim.balanceDue?.amount ? parseMoney(claim.balanceDue.amount) : 0;
            const providerClaim = claim.id ? providerClaimsById.get(claim.id) : undefined;

            return {
                claim,
                card: {
                    id: claim.id || claim.externalId || lines[0]?.id || 'claim',
                    num: claim.externalId || claim.id || '—',
                    statusLabel: getStatementClaimStatusLabel(claim, providerClaimsById),
                    dosLabel: formatDosLabel(claim.fromDos, claim.throughDos),
                    linesCount: lines.length,
                    providersLabel: formatProviders(claim),
                    payer: getPayerName(claim, providerClaimsById),
                    balance: formatMoney(balanceValue),
                    balanceValue,
                    patientOwes: balanceValue > 0,
                    oweAmount: balanceValue > 0 ? formatMoney(balanceValue) : undefined,
                    lines,
                    flowRows: buildFlowRows(claim, providerClaim),
                    href: getClaimRowHref && providerClaim ? getClaimRowHref(providerClaim) : undefined,
                },
            };
        })
        .filter(({ claim, card }) => matchesSearch(claim, card.lines, searchQuery))
        .sort((a, b) => {
            const aTime = a.claim.fromDos ? new Date(a.claim.fromDos).getTime() : 0;
            const bTime = b.claim.fromDos ? new Date(b.claim.fromDos).getTime() : 0;
            return bTime - aTime;
        })
        .map(({ card }) => card);
};
